import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import { RotateCcw } from "lucide-react";

/**
 * SignatureCanvas — touch + mouse capable. Returns base64 PNG via ref.
 *
 *   const sigRef = useRef();
 *   <SignatureCanvas ref={sigRef} />
 *   sigRef.current.toDataURL();   // null if empty
 *   sigRef.current.clear();
 *   sigRef.current.isEmpty();
 */
const SignatureCanvas = forwardRef(function SignatureCanvas(
  { height = 180, className = "" },
  ref
) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [isEmpty, setIsEmpty] = useState(true);

  // Resize canvas to DPR for sharp lines
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0E0E18";
    // Background — paint white so toDataURL has a real white bg
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, rect.width, rect.height);
  }, [height]);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = getPoint(e);
    setIsEmpty(false);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = getPoint(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
  };

  const end = () => {
    drawingRef.current = false;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setIsEmpty(true);
  };

  useImperativeHandle(ref, () => ({
    toDataURL: () => (isEmpty ? null : canvasRef.current.toDataURL("image/png")),
    clear,
    isEmpty: () => isEmpty,
  }));

  return (
    <div className={"relative " + className} data-testid="signature-canvas-wrap">
      <canvas
        ref={canvasRef}
        data-testid="signature-canvas"
        style={{
          width: "100%",
          height,
          borderRadius: 6,
          border: "1px dashed var(--line-strong)",
          background: "#fff",
          touchAction: "none",
          display: "block",
        }}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      <button
        type="button"
        data-testid="signature-clear-btn"
        onClick={clear}
        className="absolute top-2 right-2 flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.12em] px-2 py-1 rounded-md"
        style={{ background: "rgba(14,14,24,0.06)", color: "var(--ink-soft)" }}
      >
        <RotateCcw size={11} /> Clear
      </button>
      {isEmpty && (
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none text-[12px]"
          style={{ color: "var(--ink-faint)" }}
        >
          Sign here with finger or mouse
        </div>
      )}
    </div>
  );
});

export default SignatureCanvas;
